export type Serializable =
  | string
  | number
  | boolean
  | null
  | object
  | Serializable[];

const SECRET_KEY =
  process.env.NEXT_PUBLIC_CRYPTO_SECRET_KEY || "default-secret-key-change-me";

// Polyfill/Utility for Web Crypto across environments
const getCrypto = async () => {
  if (typeof globalThis !== "undefined" && globalThis.crypto) {
    return globalThis.crypto;
  }
  // For Node.js < 19 - Use dynamic import to satisfy ESLint and bundlers
  try {
    const nodeCrypto = await import("node:crypto");
    return nodeCrypto.webcrypto as unknown as Crypto;
  } catch {
    console.error("Web Crypto API not found.");
    return null;
  }
};

/**
 * Derives a crypto key from the secret key and salt.
 */
const deriveKey = async (salt: Uint8Array): Promise<CryptoKey> => {
  const crypto = await getCrypto();
  if (!crypto || !crypto.subtle) throw new Error("Crypto API not available");
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(SECRET_KEY) as unknown as Uint8Array<ArrayBuffer>,
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );

  return await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt as unknown as Uint8Array<ArrayBuffer>,
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
};

/**
 * Encrypts a string or object into a Base64 string.
 */
export const encryptData = async (data: Serializable): Promise<string> => {
  try {
    const crypto = await getCrypto();
    if (!crypto) throw new Error("Crypto API not available");
    const stringData = typeof data === "string" ? data : JSON.stringify(data);
    const enc = new TextEncoder();
    const encodedData = enc.encode(stringData);

    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const aesKey = await deriveKey(salt);

    const encryptedContent = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: iv as unknown as Uint8Array<ArrayBuffer> },
      aesKey,
      encodedData as unknown as Uint8Array<ArrayBuffer>,
    );

    const encryptedArray = new Uint8Array(encryptedContent);
    const combined = new Uint8Array(
      salt.length + iv.length + encryptedArray.length,
    );
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(encryptedArray, salt.length + iv.length);

    // Convert to Base64 (compatible with both Node.js and Browser)
    if (typeof Buffer !== "undefined") {
      return Buffer.from(combined).toString("base64");
    } else {
      return btoa(String.fromCharCode(...combined));
    }
  } catch (error) {
    console.error("Encryption error:", error);
    return "";
  }
};

/**
 * Decrypts a Base64 string back into its original form.
 */
export const decryptData = async <T = Serializable>(
  ciphertext: string,
): Promise<T | null> => {
  try {
    const crypto = await getCrypto();
    if (!crypto) throw new Error("Crypto API not available");
    let combined: Uint8Array;
    if (typeof Buffer !== "undefined") {
      combined = new Uint8Array(Buffer.from(ciphertext, "base64"));
    } else {
      combined = new Uint8Array(
        atob(ciphertext)
          .split("")
          .map((c) => c.charCodeAt(0)),
      );
    }

    const salt = combined.slice(0, 16);
    const iv = combined.slice(16, 28);
    const encryptedData = combined.slice(28);

    const aesKey = await deriveKey(salt);

    const decryptedContent = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: iv as unknown as Uint8Array<ArrayBuffer> },
      aesKey,
      encryptedData as unknown as Uint8Array<ArrayBuffer>,
    );

    const dec = new TextDecoder();
    const decryptedData = dec.decode(decryptedContent);

    try {
      return JSON.parse(decryptedData) as T;
    } catch {
      return decryptedData as T;
    }
  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
};
